import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronRight, CreditCard, RefreshCw, AlertTriangle,
  Globe, Search, MapPin, Phone, Megaphone, Calendar,
  ArrowRight, CheckCircle, Smartphone, Monitor
} from 'lucide-react';
import { mockNews, mockQuickServices } from '../mock';

const iconMap = {
  CreditCard, RefreshCw, AlertTriangle,
  Globe, Search, MapPin, Phone, DollarSign: Phone
};

const BANNERS = [
  {
    id: 1,
    bg: '#003580',
    title: 'Semak Lesen Memandu Anda',
    subtitle: 'Dapatkan maklumat lengkap tentang lesen memandu anda termasuk tarikh luput, kelas lesen dan mata SMK',
    cta: 'Semak Sekarang',
    ctaPath: '/semak-lesen',
    badge: 'Perkhidmatan Digital'
  },
  {
    id: 2,
    bg: '#004499',
    title: 'Lesen Memandu Digital 2025',
    subtitle: 'Mulai Julai 2025, gunakan lesen memandu digital melalui aplikasi MyJPJ. Mudah, selamat dan sentiasa bersama anda',
    cta: 'Ketahui Lebih Lanjut',
    ctaPath: '/',
    badge: 'Baharu'
  },
  {
    id: 3,
    bg: '#001a5c',
    title: 'Sistem Mata Kesalahan (SMK)',
    subtitle: 'Fahami sistem mata kesalahan lesen memandu Malaysia dan cara mengelak pengurangan mata SMK anda',
    cta: 'Baca Lanjut',
    ctaPath: '/',
    badge: 'Info'
  }
];

const Home = ({ language }) => {
  const [bannerIdx, setBannerIdx] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => {
      setBannerIdx(prev => (prev + 1) % BANNERS.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);
  // BANNERS is a module-level constant so its length never changes; the empty
  // deps array is intentional. setBannerIdx is stable per React contract.

  const banner = BANNERS[bannerIdx];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f5f6fa' }}>
      {/* Hero Banner */}
      <div
        className="w-full py-16 px-4 transition-colors duration-700"
        style={{ backgroundColor: banner.bg }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-white max-w-xl">
              <span className="inline-block px-3 py-1 text-xs font-bold rounded mb-4"
                style={{ backgroundColor: '#cc0000' }}>
                {banner.badge}
              </span>
              <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
                {banner.title}
              </h1>
              <p className="text-sm md:text-base opacity-80 mb-6 leading-relaxed">
                {banner.subtitle}
              </p>
              <Link
                to={banner.ctaPath}
                className="inline-flex items-center gap-2 px-6 py-3 rounded font-semibold text-sm transition-all"
                style={{ backgroundColor: '#cc0000' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#aa0000'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#cc0000'}
              >
                {banner.cta}
                <ArrowRight size={16} />
              </Link>
            </div>

            {/* Stats Card */}
            <div className="bg-white/10 backdrop-blur rounded-xl p-6 min-w-64">
              <h3 className="text-white/80 text-sm font-medium mb-4">
                {language === 'bm' ? 'Statistik Hari Ini' : "Today's Statistics"}
              </h3>
              <div className="space-y-3">
                {[
                  { label: language === 'bm' ? 'Pengguna Dalam Talian' : 'Online Users', value: '7,205' },
                  { label: language === 'bm' ? 'Semakan Dilakukan' : 'Checks Performed', value: '1,832' },
                  { label: language === 'bm' ? 'Lesen Diperbaharui' : 'Licenses Renewed', value: '456' },
                  { label: language === 'bm' ? 'Jumlah Pengunjung' : 'Total Visitors', value: '28,188,715' },
                ].map((stat) => (
                  <div key={stat.label} className="flex items-center justify-between">
                    <span className="text-white/70 text-xs">{stat.label}</span>
                    <span className="text-white font-bold text-sm">{stat.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Banner Indicators */}
          <div className="flex justify-center gap-2 mt-8">
            {BANNERS.map((b, i) => (
              <button
                key={b.id}
                onClick={() => setBannerIdx(i)}
                className={`rounded-full transition-all ${
                  i === bannerIdx ? 'w-6 h-2 bg-yellow-400' : 'w-2 h-2 bg-white/40'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Quick Search Box */}
      <div style={{ backgroundColor: '#003580' }} className="py-6 shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <span className="text-white font-semibold text-sm whitespace-nowrap">
              {language === 'bm' ? 'Semak Lesen Memandu:' : 'Check Driving License:'}
            </span>
            <div className="flex flex-1 gap-2 w-full">
              <input
                type="text"
                placeholder={language === 'bm' ? 'Masukkan No. Kad Pengenalan (Contoh: 860512035678)' : 'Enter IC Number (e.g., 860512035678)'}
                className="flex-1 px-4 py-2.5 rounded text-sm outline-none text-gray-800"
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const val = e.target.value.replace(/-/g, '');
                    if (val) navigate(`/semak-lesen?ic=${val}`);
                  }
                }}
              />
              <button
                className="flex items-center gap-2 px-5 py-2.5 rounded font-semibold text-sm text-white transition-all whitespace-nowrap"
                style={{ backgroundColor: '#cc0000' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#aa0000'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#cc0000'}
                onClick={() => navigate('/semak-lesen')}
              >
                <Search size={16} />
                {language === 'bm' ? 'Semak' : 'Check'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1 text-xs text-gray-500 mb-6">
          <span>{language === 'bm' ? 'Laman Utama' : 'Home'}</span>
        </div>

        {/* Announcement Bar */}
        <div className="flex items-start gap-3 bg-blue-50 border-l-4 border-blue-600 px-4 py-3 rounded mb-8 text-sm">
          <Megaphone size={16} className="mt-0.5 flex-shrink-0 text-blue-600" />
          <div>
            <span className="font-bold text-blue-800 mr-2">{language === 'bm' ? 'Pengumuman:' : 'Announcement:'}</span>
            <span className="text-blue-700">
              {language === 'bm'
                ? 'JPJ akan menjalankan penyelenggaraan sistem pada 25 Mei 2025 (Ahad), 12:00 tengah malam - 6:00 pagi. Perkhidmatan dalam talian tidak dapat diakses pada waktu tersebut.'
                : 'JPJ will conduct system maintenance on 25 May 2025 (Sunday), 12:00 midnight – 6:00 am. Online services will be unavailable during this period.'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left/Main - Quick Services + News */}
          <div className="lg:col-span-2 space-y-8">
            {/* Quick Services */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-gray-800 text-lg border-l-4 pl-3" style={{ borderColor: '#003580' }}>
                  {language === 'bm' ? 'Perkhidmatan Pantas' : 'Quick Services'}
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { icon: CreditCard, title: language === 'bm' ? 'Semak Lesen Memandu' : 'Check License', path: '/semak-lesen', color: '#003580' },
                  { icon: RefreshCw, title: language === 'bm' ? 'Pembaharuan Lesen' : 'Renew License', path: '/pembaharuan', color: '#003580' },
                  { icon: AlertTriangle, title: language === 'bm' ? 'Semak Saman' : 'Check Summons', path: '/semak-saman', color: '#cc0000' },
                  { icon: Globe, title: language === 'bm' ? 'Lesen Asing' : 'Foreign License', path: '/pertukaran', color: '#009900' },
                  { icon: Search, title: language === 'bm' ? 'Status Permohonan' : 'App. Status', path: '/status', color: '#009900' },
                  { icon: MapPin, title: language === 'bm' ? 'Cari Cawangan' : 'Find Branch', path: '/cawangan', color: '#666' },
                  { icon: Phone, title: language === 'bm' ? 'Hubungi Kami' : 'Contact Us', path: '/hubungi', color: '#666' },
                  { icon: Monitor, title: 'MyJPJ', path: '/', color: '#0066cc' },
                ].map((service, i) => (
                  <Link
                    key={i}
                    to={service.path}
                    className="flex flex-col items-center justify-center gap-2 bg-white rounded-lg p-4 text-center shadow-sm hover:shadow-md transition-all group border border-gray-100"
                    style={{ minHeight: 96 }}
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center transition-all group-hover:scale-110"
                      style={{ backgroundColor: `${service.color}15` }}
                    >
                      <service.icon size={20} style={{ color: service.color }} />
                    </div>
                    <span className="text-xs font-medium text-gray-700 leading-tight text-center">
                      {service.title}
                    </span>
                  </Link>
                ))}
              </div>
            </section>

            {/* Latest News */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-gray-800 text-lg border-l-4 pl-3" style={{ borderColor: '#cc0000' }}>
                  {language === 'bm' ? 'Berita & Pengumuman' : 'News & Announcements'}
                </h2>
                <a href="#" className="text-xs font-medium hover:underline" style={{ color: '#003580' }}>
                  {language === 'bm' ? 'Lihat Semua' : 'View All'}
                </a>
              </div>
              <div className="space-y-3">
                {mockNews.map(news => (
                  <div
                    key={news.id}
                    className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-all cursor-pointer border border-gray-100 group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <div
                          className="w-8 h-8 rounded flex items-center justify-center"
                          style={{ backgroundColor: '#003580' }}
                        >
                          <Calendar size={14} className="text-white" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: '#003580', color: 'white' }}>
                            {news.category}
                          </span>
                          <span className="text-xs text-gray-400">{news.date}</span>
                        </div>
                        <h3 className="font-semibold text-gray-800 text-sm group-hover:text-blue-700 transition-colors leading-snug">
                          {news.title}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{news.excerpt}</p>
                      </div>
                      <ChevronRight size={16} className="flex-shrink-0 text-gray-400 group-hover:text-blue-600 mt-1 transition-colors" />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* MyJPJ App Promo */}
            <div
              className="rounded-xl p-5 text-white"
              style={{ background: 'linear-gradient(135deg, #003580, #0066cc)' }}
            >
              <Smartphone size={28} className="mb-3" />
              <h3 className="font-bold text-sm mb-2">Aplikasi MyJPJ</h3>
              <p className="text-xs opacity-80 mb-4 leading-relaxed">
                {language === 'bm'
                  ? 'Muat turun aplikasi MyJPJ untuk akses mudah kepada semua perkhidmatan JPJ di hujung jari anda.'
                  : 'Download the MyJPJ app for easy access to all JPJ services at your fingertips.'}
              </p>
              <div className="flex gap-2">
                <button
                  className="flex-1 text-xs py-2 px-3 rounded font-semibold transition-all"
                  style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                >
                  App Store
                </button>
                <button
                  className="flex-1 text-xs py-2 px-3 rounded font-semibold transition-all"
                  style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                >
                  Google Play
                </button>
              </div>
            </div>

            {/* Branch Info */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-bold text-gray-800 text-sm mb-3 flex items-center gap-2">
                <MapPin size={16} style={{ color: '#003580' }} />
                {language === 'bm' ? 'Waktu Operasi' : 'Operation Hours'}
              </h3>
              <div className="space-y-2 text-xs">
                {[
                  { day: language === 'bm' ? 'Isnin - Jumaat' : 'Monday - Friday', time: '8:00 pagi - 5:00 petang' },
                  { day: language === 'bm' ? 'Sabtu' : 'Saturday', time: '8:00 pagi - 12:30 tengah hari' },
                  { day: language === 'bm' ? 'Ahad & Cuti Umum' : 'Sunday & Public Holidays', time: language === 'bm' ? 'Tutup' : 'Closed' },
                ].map((item) => (
                  <div key={item.day} className="flex justify-between">
                    <span className="text-gray-500">{item.day}</span>
                    <span className="font-medium text-gray-700">{item.time}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* SMK Info */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-bold text-gray-800 text-sm mb-3">
                {language === 'bm' ? 'Sistem Mata Kesalahan (SMK)' : 'Traffic Demerit System (SMK)'}
              </h3>
              <div className="space-y-2">
                {[
                  { points: '0–6', desc: language === 'bm' ? 'Amaran diberikan' : 'Warning issued', color: '#f59e0b' },
                  { points: '7–13', desc: language === 'bm' ? 'Kursus wajib' : 'Compulsory course', color: '#f97316' },
                  { points: '14–20', desc: language === 'bm' ? 'Amaran akhir' : 'Final warning', color: '#ef4444' },
                  { points: '>20', desc: language === 'bm' ? 'Lesen digantung' : 'License suspended', color: '#7f1d1d' },
                ].map((item) => (
                  <div key={item.points} className="flex items-center justify-between text-xs">
                    <span className="font-bold px-2 py-0.5 rounded text-white" style={{ backgroundColor: item.color }}>
                      {item.points} mata
                    </span>
                    <span className="text-gray-600">{item.desc}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">
                {language === 'bm' ? '* Kira semula setiap 3 tahun' : '* Reset every 3 years'}
              </p>
            </div>

            {/* Visitor Counter */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
              <p className="text-xs text-gray-400 mb-1">{language === 'bm' ? 'Jumlah Pengunjung' : 'Total Visitors'}</p>
              <p className="font-black text-2xl" style={{ color: '#003580' }}>28,188,715</p>
              <p className="text-xs text-gray-400 mt-1">{language === 'bm' ? 'Pengunjung hari ini: 7,205' : "Today's visitors: 7,205"}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
